import type { FC, ReactNode } from 'react'
import type { NavItem } from '@/types'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { ChevronDown, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { BookingButton } from '@/components/generals/button-link'
import { mainNav } from '@/constants/navigation'

const isActive = (pathname: string, item: NavItem): boolean => {
    if (pathname === item.href) return true

    return Boolean(item.children?.some((child) => child.href === pathname))
}

const desktopLinkClasses = (active: boolean): string => {
    return cn(
        'rounded-full px-3.5 py-2 text-[0.95rem] font-medium transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-plum',
        active ? 'text-plum underline decoration-honey decoration-2 underline-offset-[10px]' : 'text-espresso hover:text-plum'
    )
}

interface NavDropdownProps {
    item: NavItem
    pathname: string
}

const NavDropdown: FC<NavDropdownProps> = ({ item, pathname }): ReactNode => {
    const router = useRouter()
    const [open, setOpen] = useState(false)
    const containerRef = useRef<HTMLLIElement>(null)
    const toggleRef = useRef<HTMLButtonElement>(null)
    const menuId = `${item.label.toLowerCase().replace(/\s+/g, '-')}-submenu`

    useEffect(() => {
        const close = (): void => setOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    useEffect(() => {
        if (!open) return

        const handlePointerDown = (event: PointerEvent): void => {
            if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
        }

        const handleKeyDown = (event: KeyboardEvent): void => {
            if (event.key !== 'Escape') return

            const focusWasInside = containerRef.current?.contains(document.activeElement)

            setOpen(false)

            if (focusWasInside) toggleRef.current?.focus()
        }

        document.addEventListener('pointerdown', handlePointerDown)
        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.removeEventListener('pointerdown', handlePointerDown)
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [open])

    return (
        <li
            ref={containerRef}
            className='relative'
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false)
            }}
        >
            <div className='flex items-center'>
                <Link
                    href={item.href}
                    aria-current={pathname === item.href ? 'page' : undefined}
                    className={cn(desktopLinkClasses(isActive(pathname, item)), 'pr-1.5')}
                >
                    {item.label}
                </Link>

                <button
                    ref={toggleRef}
                    type='button'
                    aria-expanded={open}
                    aria-controls={menuId}
                    onClick={() => setOpen((value) => !value)}
                    className='grid size-8 place-items-center rounded-full text-espresso-soft transition-colors hover:bg-lavender/70 hover:text-plum focus-visible:outline-2 focus-visible:outline-plum'
                >
                    <ChevronDown className={cn('size-4 transition-transform duration-200', open && 'rotate-180')} aria-hidden='true' />
                    <span className='sr-only'>Show {item.label} pages</span>
                </button>
            </div>

            <div id={menuId} hidden={!open} className='absolute left-0 top-full z-50 pt-3'>
                <ul className='w-80 rounded-2xl border border-border bg-card p-2 shadow-xl shadow-plum/10'>
                    {item.children?.map((child) => (
                        <li key={child.href}>
                            <Link
                                href={child.href}
                                aria-current={pathname === child.href ? 'page' : undefined}
                                onClick={() => setOpen(false)}
                                className={cn(
                                    'block rounded-xl px-4 py-3 transition-colors hover:bg-lavender/60 focus-visible:bg-lavender/60 focus-visible:outline-2 focus-visible:outline-plum',
                                    pathname === child.href && 'bg-lavender/50'
                                )}
                            >
                                <span className='block font-medium text-plum'>
                                    {child.label}
                                </span>

                                {child.description && (
                                    <span className='mt-0.5 block text-sm text-espresso-soft'>
                                        {child.description}
                                    </span>
                                )}
                            </Link>
                        </li>
                    ))}
                </ul>
            </div>
        </li>
    )
}

export const SiteHeader: FC = (): ReactNode => {
    const router = useRouter()
    const { pathname } = router
    const [menuOpen, setMenuOpen] = useState(false)
    const menuButtonRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        const close = (): void => setMenuOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    useEffect(() => {
        if (!menuOpen) return

        const handleKeyDown = (event: KeyboardEvent): void => {
            if (event.key !== 'Escape') return

            setMenuOpen(false)
            menuButtonRef.current?.focus()
        }

        document.addEventListener('keydown', handleKeyDown)

        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [menuOpen])

    return (
        <header className='sticky top-0 z-50 border-b border-border/80 bg-shell/90 backdrop-blur-md'>
            <div className='mx-auto flex h-20 max-w-7xl items-center justify-between gap-4 px-5 sm:px-8'>
                <Logo />

                <nav aria-label='Main' className='hidden lg:block'>
                    <ul className='flex items-center gap-1'>
                        {mainNav.map((item) => item.children ? (
                            <NavDropdown key={item.href} item={item} pathname={pathname} />
                        ) : (
                            <li key={item.href}>
                                <Link
                                    href={item.href}
                                    aria-current={pathname === item.href ? 'page' : undefined}
                                    className={desktopLinkClasses(isActive(pathname, item))}
                                >
                                    {item.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='flex items-center gap-2'>
                    <BookingButton size='sm' className='hidden sm:inline-flex' />

                    <button
                        ref={menuButtonRef}
                        type='button'
                        aria-expanded={menuOpen}
                        aria-controls='mobile-menu'
                        onClick={() => setMenuOpen((value) => !value)}
                        className='grid size-11 place-items-center rounded-full text-plum transition-colors hover:bg-lavender/70 focus-visible:outline-2 focus-visible:outline-plum lg:hidden'
                    >
                        {menuOpen ? <X className='size-6' aria-hidden='true' /> : <Menu className='size-6' aria-hidden='true' />}
                        <span className='sr-only'>{menuOpen ? 'Close menu' : 'Open menu'}</span>
                    </button>
                </div>
            </div>

            <div id='mobile-menu' hidden={!menuOpen} className='border-t border-border/80 bg-shell lg:hidden'>
                <nav aria-label='Mobile' className='mx-auto max-h-[calc(100dvh-5rem)] max-w-7xl overflow-y-auto px-5 pb-8 pt-4 sm:px-8'>
                    <ul className='flex flex-col gap-1'>
                        {mainNav.map((item) => (
                            <li key={item.href}>
                                <Link
                                    href={item.href}
                                    aria-current={pathname === item.href ? 'page' : undefined}
                                    onClick={() => setMenuOpen(false)}
                                    className={cn(
                                        'flex rounded-xl px-4 py-3 font-heading text-2xl transition-colors hover:bg-lavender/60 focus-visible:outline-2 focus-visible:outline-plum',
                                        pathname === item.href ? 'bg-lavender/60 text-plum' : 'text-espresso'
                                    )}
                                >
                                    {item.label}
                                </Link>

                                {item.children && (
                                    <ul className='mb-2 ml-5 border-l-2 border-honey/70 pl-3'>
                                        {item.children.filter((child) => child.href !== item.href).map((child) => (
                                            <li key={child.href}>
                                                <Link
                                                    href={child.href}
                                                    aria-current={pathname === child.href ? 'page' : undefined}
                                                    onClick={() => setMenuOpen(false)}
                                                    className={cn(
                                                        'flex rounded-xl px-4 py-2.5 text-base font-medium transition-colors hover:bg-lavender/60 focus-visible:outline-2 focus-visible:outline-plum',
                                                        pathname === child.href ? 'bg-lavender/60 text-plum' : 'text-espresso-soft'
                                                    )}
                                                >
                                                    {child.label}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        ))}
                    </ul>

                    <BookingButton size='lg' className='mt-6 w-full' />
                </nav>
            </div>
        </header>
    )
}
